import api from './api';

const normalizeGender = (value) => {
  const normalized = String(value || '').trim().toUpperCase();
  return normalized === 'M' || normalized === 'F' ? normalized : '';
};

const normalizePhone = (value) => {
  if (value === null || value === undefined) {
    return '';
  }

  return String(value).trim();
};

const normalizeEmail = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  if (!normalized) {
    return '';
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(normalized) ? normalized : '';
};

export const buildHiddenCustomerData = (clientData = {}) => ({
  personalInfo: {
    firstname: clientData.firstname || clientData.firstName || '',
    lastname: clientData.lastname || clientData.lastName || '',
    gender: normalizeGender(clientData.gender),
    birthdate: clientData.birthdate || clientData.birthDate || '',
    cuil: String(clientData.cuit || clientData.cuil || clientData.cuil_cuit || '').replace(/\D+/g, '')
  },
  addressContact: {
    mobile: normalizePhone(clientData.phone || clientData.mobile || ''),
    email: normalizeEmail(clientData.email || '')
  }
});

export const DEFAULT_PRODUCT_REQUIREMENTS = {
  requires_employer_data: true,
  requires_employment_status: true,
  requires_income_data: true,
  labels: {
    income_label: 'Salario neto',
    bank_label: 'Banco donde cobras tu sueldo'
  }
};

export const normalizeProductRequirements = (value) => {
  if (!value || typeof value !== 'object') {
    return { ...DEFAULT_PRODUCT_REQUIREMENTS, labels: { ...DEFAULT_PRODUCT_REQUIREMENTS.labels } };
  }

  return {
    requires_employer_data: value.requires_employer_data !== false,
    requires_employment_status: value.requires_employment_status !== false,
    requires_income_data: value.requires_income_data !== false,
    labels: {
      income_label: value.labels?.income_label || DEFAULT_PRODUCT_REQUIREMENTS.labels.income_label,
      bank_label: value.labels?.bank_label || DEFAULT_PRODUCT_REQUIREMENTS.labels.bank_label
    },
    ...(value.product_id ? { product_id: value.product_id } : {}),
    ...(value.source ? { source: value.source } : {})
  };
};

export const normalizeInitialEvaluation = (value) => {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const allowedStatuses = ['processing', 'approved', 'rejected', 'technical_failure'];
  const status = allowedStatuses.includes(value.status) ? value.status : 'technical_failure';

  return {
    status,
    can_continue: status === 'approved' && value.can_continue === true,
    retryable: value.retryable === true,
    message: typeof value.message === 'string' ? value.message : null,
    poll_after_ms: value.poll_after_ms !== null
      && value.poll_after_ms !== undefined
      && Number.isFinite(Number(value.poll_after_ms))
      ? Number(value.poll_after_ms)
      : null
  };
};

export const extractProductRequirementsFromResponse = (responseData = {}) =>
  normalizeProductRequirements(
    responseData?.product_requirements
    || responseData?.data?.product_requirements
  );

export const fetchRequestRequirements = async (requestUid) => {
  try {
    const response = await api.get(`/requests/${requestUid}/requirements`);
    return extractProductRequirementsFromResponse(response.data?.data || response.data);
  } catch (error) {
    console.error('Error fetching request requirements:', error);
    return normalizeProductRequirements();
  }
};

export const fetchRequestSetupCatalog = async () => {
  const response = await api.get('/catalog/request-setup');
  return response.data?.data || {};
};

export const fetchLatestCustomerRequest = async (applicantId) => {
  const response = await api.get(`/customers/${applicantId}/requests/latest`);
  return response.data?.data || null;
};

export const fetchRequests = async ({ statuses = [], customerDocument = '', limit = 50 } = {}) => {
  const response = await api.get('/requests', {
    params: {
      statuses: Array.isArray(statuses) && statuses.length > 0 ? statuses.join(',') : undefined,
      customer_document: customerDocument || undefined,
      limit
    }
  });
  return response.data?.data || [];
};

export const fetchRequestsCount = async ({ statuses = [], customerDocument = '' } = {}) => {
  const response = await api.get('/requests/count', {
    params: {
      statuses: Array.isArray(statuses) && statuses.length > 0 ? statuses.join(',') : undefined,
      customer_document: customerDocument || undefined
    }
  });
  const count = response.data?.data?.count;
  return typeof count === 'number' ? count : 0;
};

export const fetchRequestsSummary = async () => {
  const response = await api.get('/requests/summary');
  return response.data?.data || {};
};

export const markRejectedRequestsSeen = async () => {
  const response = await api.post('/requests/rejected/mark-seen');
  return response.data?.data || {};
};

export const fetchApprovedRequests = async () =>
  fetchRequests({
    statuses: [14, 8]
  });

export const fetchApprovedRequestsCount = async () =>
  fetchRequestsCount({
    statuses: [14, 8]
  });

export const discardApprovedRequest = async (requestUid) => {
  const response = await api.post(`/requests/${requestUid}/discard`);
  return response.data?.data || {};
};

export const createComersRequest = async ({ clientData, requestSetup, hiddenCustomerData = null }) => {
  const normalizedHiddenData = hiddenCustomerData || buildHiddenCustomerData(clientData);
  const personalInfo = {
    cuil: normalizedHiddenData.personalInfo?.cuil || '',
    firstname: normalizedHiddenData.personalInfo?.firstname || '',
    lastname: normalizedHiddenData.personalInfo?.lastname || '',
    birthdate: normalizedHiddenData.personalInfo?.birthdate || '',
    ...(normalizedHiddenData.personalInfo?.gender ? { gender: normalizedHiddenData.personalInfo.gender } : {})
  };
  const addressContact = {
    ...(normalizedHiddenData.addressContact?.mobile ? { mobile: normalizedHiddenData.addressContact.mobile } : {}),
    ...(normalizedHiddenData.addressContact?.email ? { email: normalizedHiddenData.addressContact.email } : {})
  };

  const payload = {
    personalInfo,
    addressContact,
    employmentInfo: {
      employmentStatus: requestSetup.employmentStatus,
      bank: requestSetup.bank,
      monthlyIncome: Number(requestSetup.salary || 0),
      commitments: Number(requestSetup.commitments || 0),
      provinceId: Number(requestSetup.province || 0) || null,
      ...(requestSetup.companyIdentification
        ? { companyIdentification: String(requestSetup.companyIdentification).replace(/\D+/g, '') }
        : {}),
      ...(requestSetup.joinDate ? { joinDate: requestSetup.joinDate } : {})
    }
  };

  const response = await api.post('/requests', payload);
  const data = response.data?.data || {};

  return {
    ...data,
    employment: data.employment || null,
    initial_evaluation: normalizeInitialEvaluation(data.initial_evaluation),
    requestStatus: response.data?.requestStatus || null
  };
};

export const mapTargetEmploymentToFormData = (targetEmployment) => {
  if (!targetEmployment || typeof targetEmployment !== 'object') {
    return {};
  }

  return {
    targetEmployment,
    laborDataId: targetEmployment.labor_data_id,
    companyIdentification: targetEmployment.company_identification || '',
    entryDate: targetEmployment.join_date || ''
  };
};

export const fetchRequestStatus = async (requestUid) => {
  const response = await api.get(`/requests/${requestUid}/status`);
  return {
    requestStatus: response.data?.requestStatus || null,
    productRequirements: extractProductRequirementsFromResponse(response.data?.data || {})
  };
};

export const fetchDataEntryCompletionStatus = async (requestUid) => {
  const response = await api.get(`/requests/${requestUid}/data-entry/completion-status`);
  return response.data?.data || {};
};

export const fetchRequestResumeContext = async (requestUid) => {
  const response = await api.get(`/requests/${requestUid}/resume-context`);
  const data = response.data?.data || {};

  return {
    ...data,
    initial_evaluation: normalizeInitialEvaluation(data.initial_evaluation),
    requestStatus: response.data?.requestStatus || null,
    productRequirements: extractProductRequirementsFromResponse(data)
  };
};

export const fetchRequestOffers = async (requestUid) => {
  const response = await api.get(`/requests/${requestUid}/offers`);
  return response.data?.data || {};
};

export const selectRequestPreOffer = async ({ requestUid, offerId, amount, installmentAmount, installments, rate }) => {
  const response = await api.post(`/requests/${requestUid}/selected-preoffer`, {
    offerId,
    selectedAmount: Number(amount || 0),
    selectedInstallmentAmount: Number(installmentAmount || 0),
    selectedInstallments: Number(installments || 0),
    selectedRate: Number(rate || 0)
  });

  return {
    ...(response.data?.data || {}),
    requestStatus: response.data?.requestStatus || null
  };
};

export const selectRequestFinalOffer = async ({ requestUid, offerId, amount, installmentAmount, riskOfferVersion }) => {
  const response = await api.post(`/requests/${requestUid}/selected-offer`, {
    offerId,
    selectedAmount: Number(amount || 0),
    selectedInstallmentAmount: Number(installmentAmount || 0),
    riskOfferVersion: riskOfferVersion || null
  });

  return {
    ...(response.data?.data || {}),
    requestStatus: response.data?.requestStatus || null
  };
};

export const submitRequestForAnalysis = async ({ requestUid, comment = '' }) => {
  const response = await api.post(`/requests/${requestUid}/submit-analysis`, {
    comment: comment || undefined
  });
  return response.data || {};
};

export const trackRequestOnboardingStep = async ({ requestUid, step, event, metadata = {} }) => {
  const response = await api.post(`/requests/${requestUid}/complete-onboarding`, {
    step,
    event,
    metadata
  });

  return response.data || {};
};

export const fetchRequestObservations = async ({ requestUid }) => {
  const response = await api.get(`/requests/${requestUid}/observations`);
  return response.data?.data || {};
};

export const fetchObservationFileCategories = async () => {
  const response = await api.get('/observations/file-categories');
  return response.data?.data?.categories || [];
};

export const fetchRequestObservationFiles = async ({ requestUid, observationId }) => {
  const response = await api.get(`/requests/${requestUid}/observations/${observationId}/files`);
  return response.data?.data?.files || [];
};

export const uploadRequestObservationFile = async ({
  requestUid,
  observationId,
  file,
  categories,
  onUploadProgress
}) => {
  const formData = new FormData();
  formData.append('file', file);
  categories.forEach((category) => {
    formData.append('categories[]', category);
  });

  const response = await api.post(
    `/requests/${requestUid}/observations/${observationId}/files`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      onUploadProgress
    }
  );

  return response.data?.data || {};
};

export const createRequestObservationNote = async ({ requestUid, observationId, content }) => {
  const response = await api.post(`/requests/${requestUid}/observations/${observationId}/notes`, {
    content
  });
  return response.data?.data || {};
};

export const replyToRequestObservationNote = async ({ requestUid, observationId, noteId, content }) => {
  const response = await api.post(`/requests/${requestUid}/observations/${observationId}/notes/${noteId}/reply`, {
    content
  });
  return response.data?.data || {};
};

export const fetchLatestContract = async (requestUid) => {
  const response = await api.get(`/requests/${requestUid}/contracts/latest`);
  return response.data?.data || {};
};

export const signLatestContract = async (requestUid, payload) => {
  const response = await api.post(`/requests/${requestUid}/contracts/signature`, payload);
  return response.data?.data || {};
};

export const uploadComersPaperSignature = async (requestUid, file) => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await api.post(
    `/requests/${requestUid}/contracts/paper-signature`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    }
  );

  return response.data?.data || response.data || {};
};

export const setComersSignatureMethod = async (requestUid, method) => {
  const response = await api.post(`/requests/${requestUid}/contracts/signature-method`, {
    method
  });

  return response.data?.data || {};
};

export const uploadComersManualSignatureFiles = async (requestUid, filesByAlias) => {
  const formData = new FormData();

  Object.entries(filesByAlias || {}).forEach(([alias, file]) => {
    if (file) {
      formData.append(alias, file);
    }
  });

  const response = await api.post(
    `/requests/${requestUid}/contracts/manual-signature-files`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    }
  );

  return response.data?.data || response.data || {};
};

export const confirmComersRequestAfterSignature = async (requestUid) => {
  const response = await api.post(`/requests/${requestUid}/confirm-after-signature`);

  return {
    ...(response.data?.data || {}),
    requestStatus: response.data?.requestStatus || null,
    message: response.data?.message || ''
  };
};
